import React, { useState, useMemo, useContext} from "react";
import { Table, Button, Row, Col, Form, InputGroup } from "react-bootstrap";
import { Edit3, Plus, MapPin, Building2, Home, Search } from "lucide-react";
import '../css/village/village1.css';
import { Datacontext } from "../../context/dataContext";
import Swal from 'sweetalert2';
import api from "../../api";

export const VillageS = () => {
  // We track the selected IDs to filter the next columns
  const [searchZone, setSearchZone] = useState('');
  const [searchOffice, setSearchOffice] = useState('');
  const [searchVillage, setSearchVillage] = useState('');
  const [searchLayout, setSearchLayout] = useState('');

  const [selections, setSelections] = useState({ zoneId: null, officeId: null, villageId: null, 
  layoutId: null });
  const { sVillage, sOffice, sZone, fetchSVillage, fetchSOffice, fetchSZone, sLayout, fetchSLayout } = useContext(Datacontext);

  const DATA = {
    zones: sZone,
    offices: sOffice,
    villages: sVillage,
    layout: sLayout
  };

  // --- UPDATED FILTERING LOGIC WITH SEARCH ---
  const filteredZones = useMemo(() => {
    return DATA.zones
      .filter(z => z.zoneName?.toLowerCase().includes(searchZone.toLowerCase()))
      .sort((a, b) => (a.zoneName || "").localeCompare(b.zoneName || ""));
  }, [searchZone, DATA.zones]);

  const filteredOffices = useMemo(() => {
    // First filter by Zone selection, then by Search Term, then Sort
    return DATA.offices
      .filter(o => o.zoneId === selections.zoneId)
      .filter(o => o.officeName?.toLowerCase().includes(searchOffice.toLowerCase()))
      .sort((a, b) => (a.officeName || "").localeCompare(b.officeName || ""));
  }, [selections.zoneId, searchOffice, DATA.offices]);

  const filteredVillages = useMemo(() => {
    // First filter by Office selection, then by Search Term, then Sort
    return DATA.villages
      .filter(v => v.officeId === selections.officeId)
      .filter(v => v.villageName?.toLowerCase().includes(searchVillage.toLowerCase()))
      .sort((a, b) => (a.villageName || "").localeCompare(b.villageName || ""));
  }, [selections.officeId, searchVillage, DATA.villages]);

  const filteredLayout = useMemo(() => {
  return (DATA.layout || [])
    .filter(l => l.villageId === selections.villageId)
    .filter(l => l.layoutName?.toLowerCase().includes(searchLayout.toLowerCase()))
    .sort((a, b) => (a.layoutName || "").localeCompare(b.layoutName || ""));
}, [selections.villageId, searchLayout, DATA.layout]);

  const handleAddZone = () => {
    Swal.fire({
      title: 'Add New Zone',
      input: 'text',
      inputLabel: 'Zone Name',
      inputPlaceholder: 'Enter zone name...',
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Save Zone',
      inputValidator: (value) => {
        if (!value) {
          return 'Zone name is required!';
        }
        // Regex: Allow only English letters (a-z), spaces (\s), and hyphens (-)
        const regex = /^[a-zA-Z\s-]+$/;
        if (!regex.test(value)) {
          return 'Only letters, spaces, and hyphens are allowed!';
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const newZoneName = result.value.trim(); // Trim to remove accidental leading/trailing spaces
        try {
          // Show a loading state while the API call is pending
          Swal.showLoading();
          await api.post("/add_v_s", {
            type: "zone",
            name: newZoneName.toLowerCase()
          });
          // Ensure fetchSZone exists and completes
          await fetchSZone();
          Swal.fire({
            icon: 'success',
            title: 'Added!',
            text: `${newZoneName} has been created successfully.`,
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          // Handle API errors (network issues, 400s, 500s)
          console.error("Error adding zone:", error);
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response?.data?.message || 'Something went wrong while saving the zone.',
            confirmButtonColor: '#0d6efd'
          });
        }
      }
    });
  };

  const handleEditZone = (zone) => {
    Swal.fire({
      title: 'Edit Zone',
      input: 'text',
      inputLabel: 'Update Zone Name',
      inputValue: zone.zoneName, // Pre-fills with the current name
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Update',
      inputValidator: (value) => {
        if (!value) {
          return 'Zone name cannot be empty!';
        }
        const regex = /^[a-zA-Z\s-]+$/;
        if (!regex.test(value)) {
          return 'Only letters, spaces, and hyphens are allowed!';
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const updatedName = result.value.trim();
        // Avoid API call if the name hasn't changed
        if (updatedName === zone.zoneName) return;
        try {
          Swal.showLoading();
          // Adjust the endpoint and payload based on your backend
          await api.put("/edit_v_s", {
            id: zone.id,
            type: "zone",
            newName: updatedName.toLowerCase()
          });
          await fetchSZone(); // Refresh the list
          Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: 'Zone name has been updated.',
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          console.error("Error updating zone:", error);
          Swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: error.response?.data?.message || 'Could not update the zone.',
          });
        }
      }
    });
  };

  const handleAddOffice = () => {
    if (!selections.zoneId) {
      Swal.fire('Error', 'Please select a zone first', 'error');
      return;
    }
    Swal.fire({
      title: 'Add New Office',
      input: 'text',
      inputLabel: 'Office Name',
      inputPlaceholder: 'Enter office name...',
      showCancelButton: true,
      confirmButtonColor: '#198754', // Matches Bootstrap success
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Save Office',
      inputValidator: (value) => {
        if (!value) return 'Office name is required!';
        const regex = /^[a-zA-Z\s-]+$/;
        if (!regex.test(value)) return 'Only letters, spaces, and hyphens allowed!';
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.showLoading();
          await api.post("/add_v_s", {
            type: "office",
            name: result.value.trim().toLowerCase(),
            parentId: selections.zoneId // Linking to the selected zone
          });
          await fetchSOffice(); // Refresh your office list
          Swal.fire({
            icon: 'success',
            title: 'Added!',
            text: 'Office has been created.',
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          Swal.fire('Error', error.response?.data?.message || 'Failed to add office', 'error');
        }
      }
    });
  };

  const handleEditLocation = async (item, type) => {
    // 1. Determine configuration based on type
    const isOffice = type === 'office';
    const currentName = isOffice ? item.officeName : item.villageName;
    const currentParentId = isOffice ? item.zoneId : item.officeId;
    // 2. Prepare the Parent List (e.g., if editing office, get all zones)
    const parentOptions = isOffice ? DATA.zones : DATA.offices;
    const parentLabel = isOffice ? "Zone" : "Office";
    // Create HTML string for the dropdown options
    const optionsHtml = parentOptions
      .map(p => `<option value="${p.id}" ${p.id === currentParentId ? 'selected' : ''}>
                  ${p.zoneName || p.officeName}
                </option>`)
      .join('');
    // 3. Open SweetAlert with custom HTML
    const { value: formValues } = await Swal.fire({
      title: `Edit ${type.toUpperCase()}`,
      html: `
        <div class="text-start">
          <label class="form-label fw-bold">Name</label>
          <input id="swal-input-name" class="swal2-input m-0 w-100" value="${currentName}">
          
          <label class="form-label fw-bold mt-3">${parentLabel}</label>
          <select id="swal-input-parent" class="swal2-select m-0 w-100">
            ${optionsHtml}
          </select>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      preConfirm: () => {
        const newName = document.getElementById('swal-input-name').value.trim();
        const newParentId = document.getElementById('swal-input-parent').value;
        // --- VALIDATION ---
        if (!newName) {
          Swal.showValidationMessage('Name is required');
          return false;
        }
        const regex = /^[a-zA-Z\s-]+$/;
        if (!regex.test(newName)) {
          Swal.showValidationMessage('Only letters, spaces, and hyphens allowed');
          return false;
        }
        if (!newParentId) {
          Swal.showValidationMessage(`Please select a ${parentLabel}`);
          return false;
        }
        return { newName, newParentId };
      }
    });
    // 4. Handle API Update
    if (formValues) {
      try {
        Swal.showLoading();
        
        await api.put("/edit_v_s", {
          type: type,
          id: item.id,
          newName: formValues.newName.toLowerCase(),
          parentId: formValues.newParentId
        });
        // Refresh data
        isOffice ? fetchSOffice() : fetchSVillage();
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Update failed', 'error');
      }
    }
  };

  const handleAddVillage = () => {
    if (!selections.officeId) {
      Swal.fire('Wait!', 'Please select an office first', 'warning');
      return;
    }
    Swal.fire({
      title: 'Add New Village',
      input: 'text',
      inputLabel: 'Village Name',
      inputPlaceholder: 'Enter village name...',
      showCancelButton: true,
      confirmButtonColor: '#0dcaf0', // Matches Bootstrap info (bg-info)
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Save Village',
      inputValidator: (value) => {
        if (!value) return 'Village name is required!';
        const regex = /^[a-zA-Z\s-]+$/;
        if (!regex.test(value)) return 'Only letters, spaces, and hyphens allowed!';
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.showLoading();
          await api.post("/add_v_s", {
            type: "village",
            name: result.value.trim().toLowerCase(),
            parentId: selections.officeId // Links to selected office
          });
          await fetchSVillage(); // Refresh list
          Swal.fire({ icon: 'success', title: 'Added!', timer: 1500, showConfirmButton: false });
        } catch (error) {
          Swal.fire('Error', error.response?.data?.message || 'Failed to add', 'error');
        }
      }
    });
  };

  const handleEditVillage = async (village) => {
    // Generate the list of available offices for the dropdown
    /*const officeOptions = DATA.offices
      .map(off => `<option value="${off.id}" ${off.id === village.officeId ? 'selected' : ''}>
                    ${off.officeName}
                  </option>`)
      .join('');*/
      const officeOptions = DATA.offices
      .filter(off => off.zoneId === selections.zoneId) // Only offices in the current zone
      .map(off => `
        <option value="${off.id}" ${off.id === village.officeId ? 'selected' : ''}>
          ${off.officeName}
        </option>`)
      .join('');
    const { value: formValues } = await Swal.fire({
      title: 'Edit Village',
      confirmButtonColor: '#0dcaf0',
      showCancelButton: true,
      html: `
        <div class="text-start">
          <label class="form-label fw-bold">Village Name</label>
          <input id="edit-village-name" class="swal2-input m-0 w-100" value="${village.villageName}">
          
          <label class="form-label fw-bold mt-3">Belongs to Office</label>
          <select id="edit-village-parent" class="swal2-select m-0 w-100">
            ${officeOptions}
          </select>
        </div>
      `,
      preConfirm: () => {
        const name = document.getElementById('edit-village-name').value.trim();
        const parentId = document.getElementById('edit-village-parent').value;
        const regex = /^[a-zA-Z\s-]+$/;
        if (!name) return Swal.showValidationMessage('Name is required');
        if (!regex.test(name)) return Swal.showValidationMessage('Letters, spaces, and hyphens only');
        if (!parentId) return Swal.showValidationMessage('Please select an office');
        return { name, parentId };
      }
    });
    if (formValues) {
      try {
        Swal.showLoading();
        await api.put("/edit_v_s", {
          type: "village",
          id: village.id,
          newName: formValues.name.toLowerCase(),
          parentId: formValues.parentId
        });
        await fetchSVillage();
        Swal.fire({ icon: 'success', title: 'Updated!', timer: 1500, showConfirmButton: false });
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Update failed', 'error');
      }
    }
  };

  const handleAddLayout = () => {
  if (!selections.villageId) {
    Swal.fire('Wait!', 'Please select a village first', 'warning');
    return;
  }
  Swal.fire({
    title: 'Add New Layout',
    input: 'text',
    inputLabel: 'Layout Name',
    showCancelButton: true,
    confirmButtonColor: '#6f42c1', 
    inputValidator: (value) => {
      if (!value) return 'Layout name is required!';
      const regex = /^[a-zA-Z0-9\s-]+$/; // Allowed numbers for layouts (e.g., Layout 1)
      if (!regex.test(value)) return 'Letters, numbers, and hyphens only!';
    }
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        Swal.showLoading();
        await api.post("/add_v_s", {
          type: "layout",
          name: result.value.trim().toLowerCase(),
          parentId: selections.villageId
        });
        await fetchSLayout();
        Swal.fire({ icon: 'success', title: 'Added!', timer: 1500, showConfirmButton: false });
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Failed to add', 'error');
      }
    }
  });
};

const handleEditLayout = async (layout) => {
  // Only show villages belonging to the currently selected office
  const villageOptions = DATA.villages
    .filter(v => v.officeId === selections.officeId)
    .map(v => `<option value="${v.id}" ${v.id === layout.villageId ? 'selected' : ''}>
                ${v.villageName}
              </option>`)
    .join('');

  const { value: formValues } = await Swal.fire({
    title: 'Edit Layout',
    confirmButtonColor: '#6f42c1',
    showCancelButton: true,
    html: `
      <div class="text-start">
        <label class="form-label fw-bold">Layout Name</label>
        <input id="edit-layout-name" class="swal2-input m-0 w-100" value="${layout.layoutName}">
        <label class="form-label fw-bold mt-3">Belongs to Village</label>
        <select id="edit-layout-parent" class="swal2-select m-0 w-100">${villageOptions}</select>
      </div>`,
    preConfirm: () => ({
      name: document.getElementById('edit-layout-name').value.trim(),
      parentId: document.getElementById('edit-layout-parent').value
    })
  });

  if (formValues) {
    try {
      Swal.showLoading();
      await api.put("/edit_v_s", {
        type: "layout",
        id: layout.id,
        newName: formValues.name.toLowerCase(),
        parentId: formValues.parentId
      });
      await fetchSLayout();
      Swal.fire({ icon: 'success', title: 'Updated!', timer: 1500, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Error', 'Update failed', 'error');
    }
  }
};

  return (
    <div className="document-container p-4">
      <div className="mb-4">
        <h3 className="page-title mb-1">Zone Hierarchy Manager</h3>
        <p className="text-muted small">Select a zone to see offices, and an office to see villages.</p>
      </div>

      <Row className="g-3">
        {/* --- COLUMN 1: ZONES --- */}
        <Col lg={3}>
          <div className="table-card shadow-sm border-0">
            <div className="column-header d-flex justify-content-between align-items-center p-3">
              <span className="fw-bold text-white">
                <MapPin size={16} className="me-2"/>ZONES ({filteredZones.length})
              </span>
              <Button size="sm" variant="light" className="btn-circle" onClick={handleAddZone}><Plus size={14}/></Button>
            </div>
            {/* Search Input for Zones */}
            <div className="p-2 bg-light border-bottom">
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                <Form.Control 
                  className="border-start-0" 
                  placeholder="Search zone..." 
                  value={searchZone}
                  onChange={(e) => setSearchZone(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="list-scroll-container">
              <Table hover className="m-0 border-0">
                <tbody>
                  {filteredZones.map((zone) => (
                    <tr 
                      key={zone.id} 
                      onClick={() => {
                        // Reset ALL children when a new top-level parent is clicked
                        setSelections({ zoneId: zone.id, officeId: null, villageId: null, layoutId: null });
                        setSearchOffice(''); 
                        setSearchVillage('');
                        setSearchLayout('');
                      }}
                      className={`cursor-pointer ${selections.zoneId === zone.id ? 'active-row' : ''}`}
                    >
                      <td className="border-0 align-middle text-start ps-3">
                        <span className="fw-semibold text-capitalize">{zone.zoneName?.toUpperCase()}</span>
                      </td>
                      <td className="border-0 text-end pe-3">
                        <Button variant="link" className="p-0 text-primary" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditZone(zone);
                        }}><Edit3 size={16}/></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>

        {/* --- COLUMN 2: OFFICES --- */}
        <Col lg={3}>
          <div className={`table-card shadow-sm border-0 ${!selections.zoneId ? 'opacity-50' : ''}`}>
            <div className="column-header d-flex justify-content-between align-items-center p-3 bg-success">
              <span className="fw-bold text-white">
                <Building2 size={16} className="me-2"/>OFFICES ({filteredOffices.length})
              </span>
              <Button size="sm" variant="light" className="btn-circle" disabled={!selections.zoneId} onClick={handleAddOffice}><Plus size={14}/></Button>
            </div>
            {/* Search Input for Offices */}
            <div className="p-2 bg-light border-bottom">
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                <Form.Control 
                  className="border-start-0" 
                  placeholder="Search office..." 
                  disabled={!selections.zoneId}
                  value={searchOffice}
                  onChange={(e) => setSearchOffice(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="list-scroll-container">
              {selections.zoneId ? (
                <Table hover className="m-0 border-0">
                  <tbody>
                    {filteredOffices.length > 0 ? filteredOffices.map((office) => (
                      <tr 
                        key={office.id} 
                        onClick={() => {
                          // Reset Village and Layout when a new Office is clicked
                          setSelections({ ...selections, officeId: office.id, villageId: null, layoutId: null });
                          setSearchVillage(''); 
                          setSearchLayout('');
                        }}
                        className={`cursor-pointer ${selections.officeId === office.id ? 'active-row' : ''}`}
                      >
                        <td className="border-0 align-middle text-start ps-3">
                           <span className="fw-semibold text-capitalize">{office.officeName?.toUpperCase()}</span>
                        </td>
                        <td className="border-0 text-end pe-3">
                           <Button variant="link" className="p-0 text-primary"
                           onClick={(e) => {
                            e.stopPropagation();
                            handleEditLocation(office, 'office');
                          }}><Edit3 size={16}/></Button>
                        </td>
                      </tr>
                    )) : <tr><td className="text-muted p-4">No offices matching search</td></tr>}
                  </tbody>
                </Table>
              ) : <div className="p-5 text-center text-muted">Select a zone first</div>}
            </div>
          </div>
        </Col>

        {/* --- COLUMN 3: VILLAGES --- */}
        <Col lg={3}>
          <div className={`table-card shadow-sm border-0 ${!selections.officeId ? 'opacity-50' : ''}`}>
            <div className="column-header d-flex justify-content-between align-items-center p-3 bg-info">
              <span className="fw-bold text-white">
                <Home size={16} className="me-2"/>VILLAGES ({filteredVillages.length})
              </span>
              <Button size="sm" variant="light" className="btn-circle" disabled={!selections.officeId} onClick={handleAddVillage}><Plus size={14}/></Button>
            </div>
            {/* Search Input for Villages */}
            <div className="p-2 bg-light border-bottom">
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                <Form.Control 
                  className="border-start-0" 
                  placeholder="Search village..." 
                  disabled={!selections.officeId}
                  value={searchVillage}
                  onChange={(e) => setSearchVillage(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="list-scroll-container">
              {selections.officeId ? (
                <Table hover className="m-0 border-0">
                  <tbody>
                    {filteredVillages.length > 0 ? filteredVillages.map((village) => (
                      // Inside your filteredVillages.map in the Village Column:
<tr 
  key={village.id} 
  onClick={() => {
    setSelections({ 
      ...selections, 
      villageId: village.id, 
      layoutId: null // Reset child selection
    });
    setSearchLayout(''); // Clear search for the next column
  }}
  className={`cursor-pointer ${selections.villageId === village.id ? 'active-row' : ''}`}
>
                        <td className="border-0 align-middle text-start ps-3">
                           <span className="fw-semibold text-capitalize">{village.villageName?.toUpperCase()}</span>
                        </td>
                        <td className="border-0 text-end pe-3">
                           <Button 
                             variant="link" 
                             className="p-0 text-primary"
                             onClick={(e) =>{
                              e.stopPropagation(); handleEditVillage(village)
                            }}
                           >
                             <Edit3 size={16}/>
                           </Button>
                        </td>
                      </tr>
                    )) : <tr><td className="text-muted p-4">No villages matching search</td></tr>}
                  </tbody>
                </Table>
              ) : <div className="p-5 text-center text-muted">Select an office first</div>}
            </div>
          </div>
        </Col>

        {/* --- COLUMN 4: Layout --- */}
        <Col lg={3}>
          <div className={`table-card shadow-sm border-0 ${!selections.villageId ? 'opacity-50' : ''}`}>
            <div className="column-header d-flex justify-content-between align-items-center p-3" style={{backgroundColor: '#6f42c1'}}>
              <span className="fw-bold text-white">
                <Home size={16} className="me-2"/>LAYOUT ({filteredLayout.length})
              </span>
              <Button size="sm" variant="light" className="btn-circle" disabled={!selections.villageId} onClick={handleAddLayout}>
                <Plus size={14}/>
              </Button>
            </div>
            
            <div className="p-2 bg-light border-bottom">
              <InputGroup size="sm">
                <InputGroup.Text><Search size={14}/></InputGroup.Text>
                <Form.Control 
                  placeholder="Search Layout..." 
                  disabled={!selections.villageId}
                  value={searchLayout}
                  onChange={(e) => setSearchLayout(e.target.value)}
                />
              </InputGroup>
            </div>

            <div className="list-scroll-container">
              {selections.villageId ? (
                <Table hover className="m-0 border-0">
                  <tbody>
                    {filteredLayout.length > 0 ? filteredLayout.map((layout) => (
                      <tr key={layout.id}>
                        <td className="border-0 align-middle text-start ps-3">
                          <span className="fw-semibold text-capitalize">{layout.layoutName?.toUpperCase()}</span>
                        </td>
                        <td className="border-0 text-end pe-3">
                          <Button variant="link" className="p-0 text-primary" onClick={(e) =>{ e.stopPropagation(); handleEditLayout(layout); }}>
                            <Edit3 size={16}/>
                          </Button>
                        </td>
                      </tr>
                    )) : <tr><td className="text-muted p-4">No Layout found</td></tr>}
                  </tbody>
                </Table>
              ) : <div className="p-5 text-center text-muted">Select a village first</div>}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};